// Types
import {
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities,
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities_streams,
} from 'api/streamAffiliate/types/GetAffiliateCommissionPayouts';
// Helpers
import { getPublicStreamDate } from 'helpers/utils';

export type PayoutRow =
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities;
export type PayoutStreamRow =
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities_streams;

export type CommissionReportItem = {
  userId: string;
  participantName: string;
  email: string;
  salesCount: number;
  commissionTotal: number;
  unpaidCommissionTotal: number;
  streams: PayoutStreamRow[];
};

const getParticipantName = (user: PayoutRow['user']): string =>
  user.storeDetails?.storeName ||
  `${user.firstName || ''} ${user.lastName || ''}`.trim();

type Organizer = {
  firstName: string | null;
  lastName: string | null;
  slug: string | null;
};

const getOrganizerName = (store?: Organizer | null): string =>
  store
    ? `${store.firstName || ''} ${store.lastName || ''}`.trim() ||
      store.slug ||
      ''
    : '';

/** A commission comes out of a ticketed event when the row has one, otherwise out of the stream's PPV. */
export const getPayoutSource = ({
  stream,
  ticketEvent,
}: PayoutStreamRow): {
  id: string;
  saleType: 'Tickets' | 'PPV';
  name: string;
  date: string;
  organizer: string;
} => {
  if (ticketEvent) {
    return {
      id: ticketEvent.id,
      saleType: 'Tickets',
      name: ticketEvent.name,
      date: getPublicStreamDate(ticketEvent.startsAt),
      organizer: getOrganizerName(ticketEvent.host),
    };
  }

  return {
    id: stream?.id || '',
    saleType: 'PPV',
    name: stream?.name || '',
    date: stream ? getPublicStreamDate(stream.scheduleDateUtc) : '',
    organizer: getOrganizerName(stream?.store),
  };
};

export const toReportItems = (entities: PayoutRow[]): CommissionReportItem[] =>
  entities.map((row) => ({
    userId: row.user.id,
    participantName: getParticipantName(row.user),
    email: row.user.email ?? '',
    salesCount: row.salesCount,
    commissionTotal: row.commissionTotal,
    unpaidCommissionTotal: row.unpaidCommissionTotal,
    streams: row.streams,
  }));

// One CSV line per participant and event, so the organizer-side deduction is explicit.
export type CommissionCsvRow = {
  participant: string;
  email: string;
  organizer: string;
  event: string;
  eventDate: string;
  type: 'Tickets' | 'PPV';
  sales: number;
  commission: number;
  unpaidCommission: number;
};

export const toCsvRows = (items: CommissionReportItem[]): CommissionCsvRow[] =>
  items.flatMap((row) =>
    row.streams.map((streamRow) => {
      const source = getPayoutSource(streamRow);

      return {
        participant: row.participantName,
        email: row.email,
        organizer: source.organizer,
        event: source.name,
        eventDate: source.date,
        type: source.saleType,
        sales: streamRow.salesCount,
        commission: streamRow.commission,
        unpaidCommission: streamRow.unpaidCommission,
      };
    })
  );
